import React from 'react';
import { useParams } from 'react-router';
import { Text, TextVariant } from '@naf/text';
import { NotFound } from '@loadable-elements/NotFound';
import type { DocumentData } from '../../../../redux/modules/documents';
import { EventType } from '../../../../../../types/EventType';
import { DocumentTypeLabel } from '../../../../../../types/testType';
import { useDocumentData } from '../../../../hooks/Documents/useDocumentData';
import { LoaderContent } from '../../../LoaderPage';
import OrganizerHeader from '../../components/OrganizerHeader';
import EventDetails from '../../components/EventDetails';
import BlockContent from '../../../../components/block-content/BlockContent';
import { FormBlock } from '../../../../components/block-content/types/components/Form/FormBlock';
import { useAuth0Token } from '../../../../hooks/useAuth0Token';
import { useWindowLocation } from '../../../../hooks/Window/useWindowLocation';
import { useSendGTMEventOnce } from '../../../../hooks/GTM/useSendGTMEventOnce';
import { useHandleNafLocationsRoutes } from '../../useHandleNafLocationsRoutes';
import { InternalSearchCardType } from '../../../../components/layout/InternalSearchMetaData';
import { LayoutWithMainContent } from '../../../../components/layout/Layout';
import SchemaEvent from '../../../../components/structured-data/SchemaEvent';
import * as S from './EventPage.styles';

const isEvent = (data?: DocumentData): data is EventType => data?.type === DocumentTypeLabel.event;

const EventPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { simpleToken } = useAuth0Token();
  const { data, meta } = useDocumentData(slug, simpleToken);
  const url = useWindowLocation();
  const { locations } = useHandleNafLocationsRoutes();

  useSendGTMEventOnce(
    isEvent(data) && {
      page_type: 'nafLocationsApp',
      pageCategory: `Her finner du NAF / ${Object.values(locations).find((l) => url.includes(l.fullUrl))?.name} / ${
        data.title
      }`,
      contentId: slug,
    },
    [data],
  );

  if (!data && meta.isUpdating) {
    return <LoaderContent />;
  }
  if (!isEvent(data)) {
    return <NotFound />;
  }
  const {
    title,
    organizer,
    ingress,
    image,
    dateTimeIntervals,
    locationName,
    locationAddress,
    coordinates,
    availabilityInfo,
    body,
    registrationInfoText,
    dynamicRegistrationForm,
    priceNotMember,
    priceMember,
    seoConfig,
  } = data;
  return (
    <LayoutWithMainContent
      title={title}
      description={seoConfig?.introduction || ingress}
      seoDescription={seoConfig?.introduction || undefined}
      isHiddenFromSearch={seoConfig?.isHiddenFromSearch}
      seoTitle={seoConfig?.title || title}
      internalSearchMetaData={{
        cardType: InternalSearchCardType.Event,
        organizerTitle: organizer?.title,
        imageUrl: image?.url,
      }}
      imgUrl={image.url}
      url={url}
    >
      <SchemaEvent
        title={title}
        address={locationAddress}
        locationName={locationName}
        coordinates={coordinates}
        dateTimeIntervals={dateTimeIntervals}
      />
      <S.StyledGrid>
        <S.Content>
          <S.StyledBreadCrumb />
          {organizer && <OrganizerHeader organizer={organizer} />}
          <S.StyledTitle variant={TextVariant.Header1}>{title}</S.StyledTitle>
          <S.Ingress variant={TextVariant.Ingress}>{ingress}</S.Ingress>
          <S.StyledAvailabilityInfoBox availabilityInfo={availabilityInfo} organizer={organizer} />
        </S.Content>
        {image.url && <S.StyledImage image={image} />}
        <S.Content>
          <EventDetails
            dateTimeIntervals={dateTimeIntervals}
            locationName={locationName}
            locationAddress={locationAddress}
            coordinates={coordinates}
            priceNotMember={priceNotMember}
            priceMember={priceMember}
          />
          {registrationInfoText && registrationInfoText?.length > 0 && (
            <>
              <Text variant={TextVariant.Header2}>Påmelding</Text>
              <BlockContent value={registrationInfoText} />
            </>
          )}
          {dynamicRegistrationForm && (
            <>
              <S.TextNoMargin variant={TextVariant.Header2}>Påmelding</S.TextNoMargin>
              <FormBlock value={{ _key: '123', _type: 'dynamicForm', dynamicForm: dynamicRegistrationForm }} />
            </>
          )}
          <Text variant={TextVariant.Header2}>Om arrangementet</Text>
          <BlockContent value={body} />
        </S.Content>
      </S.StyledGrid>
    </LayoutWithMainContent>
  );
};

export default EventPage;
